/* eslint-disable arrow-body-style */
import React, {
  useCallback, useEffect, useMemo
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import RightModalLayout from '../../layouts/Modal/RightModalLayout';
import { WS_CALL_SIDEBAR_ACTIONS, SIDEBAR_ACTIONS } from '../../../constants/actionsConstants';
import RequestPermissionMC from './RequestPermissionMC';
import CustomerListMC from './CustomerListMC';

/**
 * WsCallClientListSidebarMC Component
 *
 * Displays a list of WhatsApp customers assigned to the current user.
 * Each customer has an action button to initiate a call,
 * which first checks for call permissions.
 *
 * @returns {React.ReactElement}
 */
const WsCallClientListSidebarMC = () => {
  const { t } = useTranslation('Inbox');
  const dispatch = useDispatch();
  const {
    currentStep,
    selectedClient
  } = useSelector((state) => state.wsCallSidebarReducer);

  useEffect(() => {
    return () => {
      dispatch({ type: WS_CALL_SIDEBAR_ACTIONS.RESET_STATE });
    };
  }, [dispatch]);

  const handleSelectClient = useCallback((client, step) => {
    dispatch({ type: WS_CALL_SIDEBAR_ACTIONS.SET_SELECTED_CLIENT, payload: client });
    dispatch({ type: WS_CALL_SIDEBAR_ACTIONS.SET_CURRENT_STEP, payload: step });
  }, [dispatch]);

  const handleClose = useCallback(() => {
    if (currentStep !== 'CLIENT_LIST') {
      dispatch({ type: WS_CALL_SIDEBAR_ACTIONS.SET_CURRENT_STEP, payload: 'CLIENT_LIST' });
    } else {
      dispatch({ type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR });
    }
  }, [currentStep, dispatch]);

  const sidebarTitle = useMemo(() => (
    currentStep === 'REQUEST_PERMISSION'
      ? t('wsCallSidebar.requestPermission')
      : t('wsCallSidebar.title')
  ), [currentStep, t]);

  const sidebarContent = useMemo(() => {
    switch (currentStep) {
      case 'REQUEST_PERMISSION':
        return <RequestPermissionMC client={selectedClient} onClose={handleClose} />;
      default:
        return <CustomerListMC onSelectClient={handleSelectClient} onClose={handleClose} />;
    }
  }, [currentStep, selectedClient, handleClose, handleSelectClient]);

  return (
    <div className="w-full">
      <RightModalLayout
        title={sidebarTitle}
        closeAction={handleClose}
        showBackButton={currentStep !== 'CLIENT_LIST'}
      >
        {sidebarContent}
      </RightModalLayout>
    </div>
  );
};

export default WsCallClientListSidebarMC;
